// src/pages/TermsOfService.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowLeft, Mail, ExternalLink } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="border-b border-[#e2e8f0] px-6 py-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-gray-500 hover:text-[#0f172a] transition">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
            <span className="text-sm text-gray-500">Input</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-6">
            <FileText className="w-12 h-12 text-[#a78bfa]" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-gray-500 text-sm">
            Effective Date: <strong>19 November 2025</strong> | Last Updated: <strong>24 March 2026</strong>
          </p>
        </div>

        <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 md:p-12 space-y-10 text-gray-600 leading-relaxed">
          <section>
            <p className="font-medium"><strong>Company:</strong> Input Performance Ltd ("we", "our", "us")</p>
            <p>Registered in England and Wales</p>
            <p className="mt-4">
              <strong>Email:</strong>{" "}
              <a href="mailto:support@inputperformanceai.com" className="text-[#a78bfa] hover:underline">
                support@inputperformanceai.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">1. Agreement to Terms</h2>
            <p>These Terms of Service ("Terms") govern your access to and use of:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>The Input website and mobile applications</li>
              <li>In-app AI coaching services</li>
              <li>Metabolic testing services</li>
              <li>AI-generated nutrition plans and meal planning</li>
              <li>AI-generated training programs</li>
              <li>Wearable device integrations (Garmin, WHOOP)</li>
              <li>Social features (friends, challenges, leaderboards)</li>
              <li>Corporate wellbeing and optimisation programmes</li>
            </ul>
            <p className="mt-6">
              By accessing or using our services, you agree to be bound by these Terms.<br />
              <strong>If you do not agree, you must not use our services.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">2. Who We Are</h2>
            <p>Input Performance Ltd provides:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Metabolic testing</li>
              <li>Performance and recovery analysis</li>
              <li>AI-driven nutrition and training coaching</li>
              <li>AI-generated meal plans and training programs</li>
              <li>Wearable device integrations</li>
              <li>Social fitness features and challenges</li>
              <li>Corporate wellness and optimisation services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">3. Eligibility</h2>
            <p>You must be:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>At least 16 years old, and</li>
              <li>Legally capable of entering into a contract in the UK</li>
            </ul>
            <p className="mt-4">You must not use our services if you are under 16.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">4. Your Account</h2>
            <p>You agree to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Provide accurate information</li>
              <li>Keep login details or verification codes secure</li>
              <li>Notify us of any suspicious activity</li>
            </ul>
            <p className="mt-6">We may suspend or terminate accounts that:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Violate these Terms</li>
              <li>Misuse the service</li>
              <li>Pose security risks</li>
              <li>Provide false information</li>
              <li>Harass other users</li>
              <li>Manipulate challenges or leaderboards</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">5. Service Description</h2>
            <p>Our services include:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>In-app AI coaching for nutrition and training</li>
              <li>Personalised metabolic analytics</li>
              <li>Wearable device data integration</li>
              <li>AI-generated nutrition plans with shopping lists</li>
              <li>AI-generated training programs with periodisation</li>
              <li>Personal best (PB) tracking</li>
              <li>Social challenges and leaderboards</li>
              <li>Friend connections and community features</li>
              <li>Corporate wellbeing assessments</li>
              <li>Performance insights and weekly reviews</li>
            </ul>
            <p className="mt-4">We may update, expand, or modify features at any time.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">6. In-App Chat & Consent</h2>
            <p>By verifying your phone number, you consent to receiving AI coaching messages in the app including:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Account verification</li>
              <li>Coaching messages</li>
              <li>Daily or weekly reminders (if enabled)</li>
              <li>Performance insights</li>
              <li>Service notifications</li>
              <li>Support messages</li>
            </ul>
            <p className="font-medium mt-6">Opt-In / Opt-Out</p>
            <p>You may withdraw chat consent at any time by:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Revoking consent in Settings → all AI coaching messages stop</li>
              <li>Re-enabling chat in Settings → messages resume</li>
            </ul>
            <p>You may also disable automations in your dashboard.<br />
            If you withdraw consent, you will no longer receive AI coaching messages of any kind.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">7. AI Coaching & Limitations</h2>
            <p>Our coaching system uses Artificial Intelligence ("AI") to generate personalised insights based on your messages, metabolic and fitness data, wearable data, and your nutrition and training preferences.</p>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">7.1 AI Providers</h3>
            <p>Our AI features are powered by:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li><strong>Anthropic (Claude)</strong> — Nutrition plan generation, meal planning, shopping list creation, food image analysis for calorie and macro estimation</li>
            </ul>
            <p className="mt-4">Both providers process your data according to their respective privacy policies and enterprise data processing terms. Your data is not used to train their public models.</p>
            
            <p className="font-bold text-red-400 text-lg mt-6">Important Legal Disclaimer</p>
            <p>AI-generated messages, plans, and recommendations:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Are not written by a human unless explicitly stated</li>
              <li>Do not constitute medical, nutritional, or professional athletic advice</li>
              <li>May sometimes be incomplete, inaccurate, or unsuitable for your specific situation</li>
              <li>Should be interpreted with personal judgement</li>
              <li>May be similar to content generated for other users due to AI patterns</li>
            </ul>
            <p className="mt-6">
              You must consult a qualified healthcare professional before making major changes to diet, exercise, or health routines.<br />
              <strong>We are not liable for injuries, health issues, or adverse outcomes resulting from following AI-generated recommendations.</strong>
            </p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">7.2 Machine Learning & Readiness Scoring</h3>
            <p>In addition to third-party AI providers, we operate machine learning models that process your data to provide:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li><strong>Training Readiness Scores</strong> — Based on sleep, HRV, recovery, training load, nutrition compliance, rest days, resting heart rate, and stress levels</li>
              <li><strong>Performance Trend Analysis</strong> — Regression-based analysis of your workout metrics over time</li>
              <li><strong>Periodisation Recommendations</strong> — Suggestions for training block structure based on your goals and readiness data</li>
              <li><strong>Injury Risk Indicators</strong> — Predictive indicators based on training load patterns, recovery data, and historical trends</li>
            </ul>
            <p className="font-bold text-amber-400 mt-4">INJURY RISK DISCLAIMER</p>
            <p>Injury risk indicators are statistical estimates only. They are NOT medical diagnoses and should not replace professional medical evaluation. Always consult a healthcare professional if you have concerns about injury risk.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">8. Nutrition Plans</h2>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">8.1 Not Medical Nutrition Therapy</h3>
            <p>Our AI-generated nutrition plans:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Are general wellness guidance only</li>
              <li>Do not constitute medical nutrition therapy</li>
              <li>Are not designed to treat, cure, diagnose, or prevent any disease or medical condition</li>
              <li>Should not replace advice from a registered dietitian or medical professional for any health conditions</li>
              <li>Are not suitable for individuals with eating disorders without professional supervision</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">8.2 Allergy Warning</h3>
            <p className="font-bold text-amber-400">IMPORTANT: While we attempt to accommodate stated allergies and intolerances:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>You must always verify all ingredients for allergens before consuming any meal</li>
              <li>Cross-contamination risks exist in food preparation that we cannot account for</li>
              <li>Ingredient substitutions may introduce unexpected allergens</li>
              <li>We are not liable for allergic reactions or adverse health effects</li>
              <li>If you have severe allergies or anaphylaxis risk, consult a medical professional before following any meal plan</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">8.3 Macro & Nutritional Accuracy</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>All nutritional values (calories, protein, carbs, fat) are estimates only</li>
              <li>Actual values depend on specific ingredients, brands, and portions used</li>
              <li>Values may vary significantly based on cooking methods and preparation</li>
              <li>You should use your own judgement and tracking tools for precise nutrition management</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">8.4 Barcode Scanning & Food Data</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Nutritional data from third-party databases (Open Food Facts, Edamam) may be incomplete, inaccurate, or outdated</li>
              <li>Product formulations may change without database updates</li>
              <li>You should verify nutritional information against product packaging</li>
              <li>We are not liable for inaccuracies in third-party food databases</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">8.5 Supplement Tracking</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Supplement tracking is a logging and organisational tool only</li>
              <li>We do not recommend, endorse, or verify any supplements</li>
              <li>Supplements may interact with medications or other supplements — consult your healthcare provider</li>
              <li>Dosages are self-reported and unverified</li>
              <li>We are not liable for adverse effects from supplements you track or consume</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">9. Training Programs</h2>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">9.1 Program Generation</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>AI-generated training programs are based on your inputs, goals, and preferences</li>
              <li>Programs are general guidance, not professional athletic coaching</li>
              <li>You should adjust workouts based on how you feel, fatigue levels, and recovery status</li>
              <li>Programs do not account for pre-existing injuries or conditions unless explicitly stated</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">9.2 Personal Bests (PBs)</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>PB data you enter is self-reported and unverified</li>
              <li>We do not verify PB claims or performance records</li>
              <li>PB data is used to personalise your training recommendations and calculate workout loading</li>
              <li>Inaccurate PB data may result in inappropriate training recommendations</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">9.3 Garmin Calendar Sync</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Workout sync to Garmin is a convenience feature</li>
              <li>Sync failures or delays do not affect your training plan within our platform</li>
              <li>We are not responsible for Garmin calendar issues, sync errors, or data discrepancies</li>
              <li>You should verify synced workouts appear correctly in your Garmin device</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">10. Health & Safety Disclaimer</h2>
            <p>You acknowledge that:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Physical training carries inherent risks including injury, illness, or death</li>
              <li>You are solely responsible for ensuring exercises are performed safely and with proper form</li>
              <li>You must stop exercising immediately if you feel unwell, dizzy, experience pain, or have difficulty breathing</li>
              <li>You must seek medical advice if unsure about training suitability for your health status</li>
              <li>You should have appropriate medical clearance before beginning any new exercise program</li>
            </ul>
            <p className="mt-6"><strong>Input Performance Ltd is not responsible for injuries, accidents, health complications, or death arising from training, exercise, nutrition changes, or applying any recommendations from our services.</strong></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">11. Wearable Integrations (Garmin, WHOOP)</h2>
            <p>By connecting a wearable device, you:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Grant us permission to access the data you approve</li>
              <li>Understand this may include health and biometric data</li>
              <li>Accept that wearable data may be incomplete, inaccurate, or delayed</li>
              <li>Acknowledge that revoking access stops all future imports</li>
            </ul>
            <p className="mt-6">You may disconnect through your device settings or in your dashboard settings page.</p>
            <p className="font-medium mt-6">Third-Party API Disclaimer</p>
            <p>Garmin, WHOOP, and other wearable providers:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Are not responsible for our services or interpretations of their data</li>
              <li>Make no guarantees about accuracy, completeness, or availability of data</li>
              <li>Do not endorse Input</li>
              <li>May change their APIs at any time, affecting our integration</li>
            </ul>
            <p className="mt-6">We are not responsible for wearable device errors, API downtime, incorrect data, missing data, or interpretation errors.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">11A. Connected Fitness Equipment (Concept2 Rower)</h2>
            <p>When you connect a Concept2 rowing machine via Bluetooth Low Energy (BLE), you consent to real-time transfer of workout data including:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Stroke rate and stroke count</li>
              <li>Pace (split time per 500m)</li>
              <li>Distance rowed</li>
              <li>Power output (watts)</li>
              <li>Heart rate (if a HR monitor is connected to the rower)</li>
              <li>Interval and session duration</li>
            </ul>
            <p className="mt-4">No data is sent to Concept2 or any third party — all data stays within Input.</p>
            <p className="mt-4">Data accuracy depends on the Concept2 performance monitor and Bluetooth connectivity. Connection may be interrupted; we are not responsible for data loss during sessions.</p>
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">Equipment Safety</h3>
            <p>You are solely responsible for the safe operation of connected fitness equipment. Always follow the manufacturer's safety guidelines.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">12. Social Features & Challenges</h2>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12.1 User Conduct in Social Features</h3>
            <p>When using friend connections, challenges, or leaderboards, you agree to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Not harass, bully, threaten, or send unwanted communications to other users</li>
              <li>Not create fake accounts or impersonate other people</li>
              <li>Not manipulate challenge scores, leaderboards, or competition results</li>
              <li>Not use automated tools or bots to gain unfair advantages</li>
              <li>Respect other users' privacy and boundaries</li>
              <li>Report any abusive behaviour to support@inputperformanceai.com</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12.2 Challenge Rules</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Challenges are for friendly competition and motivation only</li>
              <li>We reserve the right to remove users from challenges for misconduct or suspected cheating</li>
              <li>Challenge results are based on data from connected devices and may have technical limitations</li>
              <li>We do not verify the accuracy of challenge scores or completion claims</li>
              <li>Challenges do not offer prizes, money, or rewards unless explicitly stated</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12.3 Friend Connections</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>By accepting a friend request, you consent to your name being visible to that friend</li>
              <li>Challenge leaderboards show participant names and scores to all participants</li>
              <li>You can remove friends or leave challenges at any time through your dashboard</li>
              <li>Removing a friend does not delete historical challenge data</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12.4 Violations</h3>
            <p>Violations of social feature rules may result in:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Removal from specific challenges</li>
              <li>Temporary suspension of social features</li>
              <li>Permanent ban from social features</li>
              <li>Account termination in severe cases</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">12A. Gamification, Streaks & Achievements</h2>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12A.1 Streaks</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>The app tracks activity streaks (training, nutrition, hydration, weigh-in, recovery, supplement logging, habits)</li>
              <li>Streaks are motivational tools with no monetary or contractual value</li>
              <li>Streaks may be affected by technical issues, time zone differences, or sync delays</li>
              <li>We are not liable for lost streaks due to app errors, server downtime, or device issues</li>
              <li>Streak freezes may be available but are not guaranteed</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12A.2 Achievements & XP</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>XP is earned through workouts, nutrition logging, streaks, and other activities</li>
              <li>XP and levels have NO monetary value and cannot be exchanged, transferred, or redeemed</li>
              <li>We reserve the right to adjust XP values and achievement criteria at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12A.3 Prestige & Titles</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Prestige titles and cosmetic items have no monetary value</li>
              <li>Prestige progress may be visible to friends and other users</li>
              <li>We may modify or remove prestige tiers at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">12A.4 General</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>All gamification features are for motivation and engagement only</li>
              <li>Manipulation of gamification systems (including falsifying activity data) may result in account suspension or termination</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">13. Partner Program</h2>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">13.1 Partner Eligibility</h3>
            <p>Partners are typically fitness professionals, coaches, personal trainers, gyms, or businesses in the health and wellness industry. We reserve the right to approve or reject partner applications at our discretion.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">13.2 Revenue Share</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Partners receive 20% of monthly subscription revenue from clients referred through their unique partner link</li>
              <li>Commissions are calculated monthly based on active subscriptions</li>
              <li>Commissions are paid monthly via bank transfer or other agreed method</li>
              <li>Minimum payout threshold may apply</li>
              <li>We reserve the right to modify commission rates with 30 days notice</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">13.3 Partner Responsibilities</h3>
            <p>Partners agree to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Not make false, misleading, or exaggerated claims about our services</li>
              <li>Comply with all applicable advertising regulations and standards</li>
              <li>Not access individual client health data, messages, or detailed metrics</li>
              <li>Maintain confidentiality of any business information shared</li>
              <li>Not use spam, unsolicited messages, or unethical marketing tactics</li>
              <li>Clearly disclose the partner relationship when promoting our services</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">13.4 Partner Termination</h3>
            <p>We may terminate partner status immediately for:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Fraudulent referrals or self-referrals</li>
              <li>Misrepresentation of our services</li>
              <li>Violation of these Terms or partner guidelines</li>
              <li>Conduct that damages our reputation</li>
              <li>Attempting to access client data without authorisation</li>
            </ul>
            <p className="mt-4">Upon termination, unpaid commissions for legitimate referrals will be paid out. Future commissions cease immediately.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">14. Payments, Subscriptions & Billing</h2>
            
            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">14.1 Pricing</h3>
            <p>Prices for subscriptions, tests, and corporate services are displayed on our website or booking pages. We may change prices with reasonable notice, and you will be notified in advance of any changes affecting your subscription.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">14.2 Subscription Billing</h3>
            <p>When subscribing:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>You authorise recurring billing via Stripe</li>
              <li>Charges occur weekly, monthly, or annually depending on your plan</li>
              <li>Subscriptions renew automatically until cancelled</li>
              <li>Failed payments may result in service interruption</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">14.3 Free Trials</h3>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Free trials provide full access to subscription features for the trial period</li>
              <li>No credit card is required to start a free trial</li>
              <li>You will be notified before the trial ends</li>
              <li>After the trial, you must subscribe to continue accessing premium features</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">14.4 Cancellations</h3>
            <p>You may cancel at any time via account settings, support email, or your Stripe customer portal. Cancellations take effect at the end of the current billing cycle. You retain access until your paid period expires.</p>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">14.5 Refunds</h3>
            <p>We do not offer refunds for:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Completed subscription periods</li>
              <li>AI coaching already delivered</li>
              <li>Nutrition plans already generated</li>
              <li>Training programs already created</li>
              <li>Metabolic tests already completed</li>
            </ul>
            <p className="mt-4">Refunds may be issued at our discretion in exceptional circumstances. Contact support@inputperformanceai.com for refund requests.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">15. Your Responsibilities</h2>
            <p>You agree to:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Provide accurate and truthful data about yourself, your health, and your fitness</li>
              <li>Use the services lawfully and in accordance with these Terms</li>
              <li>Not misuse, exploit, or interfere with the AI system</li>
              <li>Not upload or send harmful, abusive, defamatory, or illegal content</li>
              <li>Not reverse-engineer, decompile, or tamper with our software</li>
              <li>Not attempt to access other users' accounts or data</li>
              <li>Not scrape, harvest, or collect data from our platform</li>
              <li>Not use our services for any commercial purpose without authorisation</li>
            </ul>
            <p className="mt-6">You acknowledge that inaccurate or incomplete data (including health conditions, allergies, fitness levels, or PBs) may produce inaccurate or potentially dangerous recommendations.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">16. Corporate Clients</h2>
            <p>For corporate wellbeing programmes:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Reports provided to employers are anonymised and aggregated</li>
              <li>Employers cannot access individual employee data</li>
              <li>Personal data is never shared with employers without explicit employee consent</li>
              <li>Employers are responsible for informing employees about the programme and participation</li>
              <li>Employee participation is voluntary unless otherwise agreed in employment terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">17. Intellectual Property</h2>
            <p>All content, software, features, branding, and materials provided by Input remain our exclusive property or that of our licensors.</p>
            <p className="mt-4">You may not:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Copy, modify, distribute, sell, or reproduce our materials</li>
              <li>Use our branding, logos, or trademarks without written permission</li>
              <li>Claim ownership over AI-generated content</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#0f172a] mt-6">17.1 AI-Generated Content</h3>
            <p>Content generated by our AI (including meal plans, training programs, coaching messages, and insights):</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Is licensed to you for personal, non-commercial use only</li>
              <li>May not be resold, republished, or redistributed</li>
              <li>May not be claimed as your own original work</li>
              <li>Remains the intellectual property of Input Performance Ltd</li>
              <li>May be similar to content generated for other users due to the nature of AI systems</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">18. Data Export & Portability</h2>
            <p>You may export your data at any time through:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Your account dashboard (Settings â†’ Data Export)</li>
              <li>Email request to support@inputperformanceai.com</li>
            </ul>
            <p className="mt-4">Exported data includes:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Profile information</li>
              <li>Nutrition logs and meal plans</li>
              <li>Training programs and workout history</li>
              <li>Wearable data summaries</li>
              <li>Personal bests (PBs)</li>
              <li>Goal history</li>
            </ul>
            <p className="mt-4">Complex data export requests may take up to 30 days to process.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">19. Termination</h2>
            <p>We may suspend or terminate your access if you:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Violate these Terms</li>
              <li>Misuse the AI system or attempt to manipulate it</li>
              <li>Harass other users or our staff</li>
              <li>Engage in fraudulent activity</li>
              <li>Are required to be terminated by law or regulation</li>
            </ul>
            <p className="mt-4">Upon termination:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Access to all services ceases immediately</li>
              <li>Subscription payments already made remain non-refundable</li>
              <li>You may request a copy of your personal data within 30 days</li>
              <li>Your data will be deleted according to our Privacy Policy retention periods</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">20. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, Input Performance Ltd and its directors, employees, and agents are not liable for:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Injuries, illness, or death resulting from training, exercise, or nutrition changes</li>
              <li>Allergic reactions or adverse health effects from following meal plans</li>
              <li>Errors, inaccuracies, or omissions in AI-generated content</li>
              <li>Errors, inaccuracies, or omissions in wearable device data</li>
              <li>Misunderstood or misapplied AI recommendations</li>
              <li>Service interruptions involving Supabase, Anthropic, Garmin, WHOOP, Apple, or other third parties</li>
              <li>Loss of data or data breaches beyond our reasonable control</li>
              <li>Actions of other users in social features</li>
              <li>Indirect, incidental, consequential, or punitive damages</li>
            </ul>
            <p className="mt-6"><strong>Our total liability shall not exceed the amount paid by you in the previous 12 months.</strong></p>
            <p className="mt-4">Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">21. Service Availability</h2>
            <p>We aim for uninterrupted service but cannot guarantee that:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>In-app message delivery will always be successful or timely</li>
              <li>Wearable APIs will always be available or accurate</li>
              <li>Third-party services (Anthropic, Supabase, Stripe) will have zero downtime</li>
              <li>Our platform will be available 24/7 without interruption</li>
            </ul>
            <p className="mt-4">We may perform maintenance that temporarily affects service availability. We will attempt to notify users of planned maintenance in advance.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">22. Data Protection & Privacy</h2>
            <p>Your data is processed according to our <Link to="/privacy" className="text-[#a78bfa] hover:underline">Privacy Policy</Link>.<br />
            By using our services, you confirm that you have read and agree to the Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">23. Dispute Resolution</h2>
            <p>Before initiating legal proceedings, you agree to attempt to resolve disputes by:</p>
            <ul className="list-disc pl-8 mt-4 space-y-2">
              <li>Contacting us at support@inputperformanceai.com with details of your complaint</li>
              <li>Engaging in good-faith negotiation for at least 30 days</li>
            </ul>
            <p className="mt-4">If we cannot resolve the dispute informally, either party may pursue legal remedies as outlined in Governing Law below.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">24. Changes to These Terms</h2>
            <p>We may update these Terms from time to time. You will be notified of significant changes via email, in-app notification, or website notice at least 14 days before changes take effect.</p>
            <p className="mt-4">Continued use of the service after changes take effect constitutes acceptance of the revised Terms. If you do not agree to the changes, you must stop using our services and cancel your subscription.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">25. Governing Law</h2>
            <p>These Terms are governed by the laws of England and Wales.</p>
            <p className="mt-4">Any disputes shall be resolved exclusively by the courts of England and Wales.</p>
            <p className="mt-4">If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-4">26. Contact Us</h2>
            <p>
              <a href="mailto:support@inputperformanceai.com" className="text-[#a78bfa] hover:underline">support@inputperformanceai.com</a><br />
              Input Performance Ltd<br />
              5 Kympton Gardens<br />
              GU34 5JL<br />
              United Kingdom
            </p>
          </section>

          <div className="mt-16 p-10 bg-gradient-to-r from-[#a78bfa]/10 to-transparent rounded-2xl border border-[#a78bfa]/30 text-center">
            <p className="text-xl font-medium">
              Thank you for choosing Input.
            </p>
            <p className="text-gray-500 mt-4">
              We're here to help you become stronger, fitter, and faster â€” safely and legally.
            </p>
          </div>
        </div>
      </main>

      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-red-900/20 border border-red-800 rounded-2xl p-6 text-center mb-12">
          <p className="text-red-300 font-medium text-lg">
            NOT MEDICAL ADVICE â€¢ USE AT YOUR OWN RISK
          </p>
          <p className="text-red-300 mt-2">
            Our AI coaching system provides general wellness information only. It does not diagnose, treat, or replace professional medical advice. 
            You are solely responsible for your health decisions. Always consult a qualified healthcare professional before making changes to your diet, exercise, or health routine.
          </p>
        </div>
      </div>

      <footer className="text-center text-xs text-gray-500 py-8 border-t border-[#e2e8f0] mt-20">
        Â© {new Date().getFullYear()} Input. All rights reserved.
      </footer>
    </div>
  );
}
